/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {IOProfile} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_IOPROFILE,
  ADD_ADMIN_IOPROFILE_ERROR,
  ADD_ADMIN_IOPROFILE_REQUEST,
  ADD_ADMIN_IOPROFILE_RESPONSE,
  ADMIN_IOPROFILES_GRID_REFRESHED,
  AUDIT_ADMIN_IOPROFILE,
  AUDIT_ADMIN_IOPROFILE_ERROR,
  AUDIT_ADMIN_IOPROFILE_REQUEST,
  AUDIT_ADMIN_IOPROFILE_RESPONSE,
  DELETE_ADMIN_IOPROFILE,
  DELETE_ADMIN_IOPROFILE_ERROR,
  DELETE_ADMIN_IOPROFILE_REQUEST,
  DELETE_ADMIN_IOPROFILE_RESPONSE,
  DELETE_ADMIN_IOPROFILES,
  DELETE_ADMIN_IOPROFILES_ERROR,
  DELETE_ADMIN_IOPROFILES_REQUEST,
  DELETE_ADMIN_IOPROFILES_RESPONSE,
  EDIT_ADMIN_IOPROFILE,
  EDIT_ADMIN_IOPROFILE_ERROR,
  EDIT_ADMIN_IOPROFILE_REQUEST,
  EDIT_ADMIN_IOPROFILE_RESPONSE,
  EDIT_ADMIN_IOPROFILES,
  EDIT_ADMIN_IOPROFILES_ERROR,
  EDIT_ADMIN_IOPROFILES_REQUEST,
  EDIT_ADMIN_IOPROFILES_RESPONSE,
  GET_ADMIN_IOPROFILES_ERROR,
  GET_ADMIN_IOPROFILES_REQUEST,
  GET_ADMIN_IOPROFILES_RESPONSE,
  INITIALIZE_ADMIN_IOPROFILES,
  RE_INITIALIZE_ADMIN_IOPROFILES,
  SET_ADMIN_IOPROFILES_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-ioprofiles';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

interface AdminIOProfilesActionState extends AsyncState {
  selected: IOProfile | IOProfile[] | null;
  result: IOProfile | IOProfile[] | null;
  isEditing: boolean;
};

export interface AdminIOProfilesState extends PageState {
  ioprofiles: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {},
  },
  add: AdminIOProfilesActionState,
  edit: AdminIOProfilesActionState,
  audit: AdminIOProfilesActionState,
  destroy: AdminIOProfilesActionState,
  bulkEdit: AdminIOProfilesActionState,
  bulkDelete: AdminIOProfilesActionState
};

export interface AdminIOProfilesAction extends AdminAction {
  ioprofile?: IOProfile;
  ioprofiles?: IOProfile[];
};

// defines the user preference key for the admin ioprofiles page ...
const PREFERENCE = 'admin-ioprofiles';

// defines the initial state for the reducer ...
export const initialState: AdminIOProfilesState = {
  isInitialized : false,
  ioprofiles    : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkEdit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  bulkDelete : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_IOPROFILES] : (draft: AdminIOProfilesState) => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_IOPROFILES] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, ioprofiles}: AdminIOProfilesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : ioprofiles.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_ADMIN_IOPROFILES_GRID_FILTER_MODEL] : ({grid}: AdminIOProfilesState, {model}) => {
    grid.filterModel = model;
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminIOProfilesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_IOPROFILES_GRID_REFRESHED] : (
    {add, edit, audit, destroy, bulkEdit, bulkDelete}: AdminIOProfilesState
  ) => {
    add.result = null;
    edit.result = null;
    audit.result = null;
    destroy.result = null;
    bulkEdit.result = null;
    bulkDelete.result = null;
  },
  [GET_ADMIN_IOPROFILES_REQUEST] : ({ioprofiles}: AdminIOProfilesState) => {
    ioprofiles.loading = true;
  },
  [GET_ADMIN_IOPROFILES_RESPONSE] : ({ioprofiles}: AdminIOProfilesState) => {
    ioprofiles.loading = false;
  },
  [GET_ADMIN_IOPROFILES_ERROR] : (
    {ioprofiles}: AdminIOProfilesState, {error}: AdminIOProfilesAction
  ) => {
    ioprofiles.loading = false;
    ioprofiles.error   = error;
  },
  [ADD_ADMIN_IOPROFILE] : ({add}: AdminIOProfilesState, {show}: AdminIOProfilesAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_IOPROFILE_REQUEST] : ({add}: AdminIOProfilesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_IOPROFILE_RESPONSE] : ({add}: AdminIOProfilesState, {ioprofile}: AdminIOProfilesAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = ioprofile;
  },
  [ADD_ADMIN_IOPROFILE_ERROR] : ({add}: AdminIOProfilesState, {error}: AdminIOProfilesAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_ADMIN_IOPROFILE] : ({edit}: AdminIOProfilesState, {ioprofile, show}: AdminIOProfilesAction) => {
    edit.selected  = show ? ioprofile : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_IOPROFILE_REQUEST] : ({edit}: AdminIOProfilesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_IOPROFILE_RESPONSE] : ({edit}: AdminIOProfilesState,
    {ioprofile}: AdminIOProfilesAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = ioprofile;
  },
  [EDIT_ADMIN_IOPROFILE_ERROR] : ({edit}: AdminIOProfilesState, {error}: AdminIOProfilesAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [AUDIT_ADMIN_IOPROFILE] : ({audit}: AdminIOProfilesState, {ioprofile, show}: AdminIOProfilesAction) => {
    audit.selected  = show ? ioprofile : null;
    audit.isEditing = show;
    audit.result    = null;
  },
  [AUDIT_ADMIN_IOPROFILE_REQUEST] : ({audit}: AdminIOProfilesState) => {
    audit.loading = true;
    audit.error   = audit.result = null;
  },
  [AUDIT_ADMIN_IOPROFILE_RESPONSE] : ({audit}: AdminIOProfilesState, {result}: AdminIOProfilesAction) => {
    audit.loading   = false;
    audit.isEditing = false;
    audit.result    = result;
  },
  [AUDIT_ADMIN_IOPROFILE_ERROR] : ({audit}: AdminIOProfilesState, {error}: AdminIOProfilesAction) => {
    audit.loading   = false;
    audit.error     = error;
  },
  [DELETE_ADMIN_IOPROFILE] : ({destroy}: AdminIOProfilesState, {ioprofile, show}: AdminIOProfilesAction) => {
    destroy.selected  = show ? ioprofile : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_IOPROFILE_REQUEST] : ({destroy}: AdminIOProfilesState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_IOPROFILE_RESPONSE] : ({destroy}: AdminIOProfilesState,
    {ioprofile}: AdminIOProfilesAction) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = ioprofile;
  },
  [DELETE_ADMIN_IOPROFILE_ERROR] : ({destroy}: AdminIOProfilesState, {error}: AdminIOProfilesAction) => {
    destroy.loading   = false;
    destroy.error     = error;
  },
  [EDIT_ADMIN_IOPROFILES] : ({bulkEdit}: AdminIOProfilesState, {ioprofiles, show}: AdminIOProfilesAction) => {
    bulkEdit.selected  = show ? ioprofiles : null;
    bulkEdit.isEditing = show;
    bulkEdit.result    = null;
  },
  [EDIT_ADMIN_IOPROFILES_REQUEST] : ({bulkEdit}: AdminIOProfilesState) => {
    bulkEdit.loading = true;
    bulkEdit.error   = bulkEdit.result = null;
  },
  [EDIT_ADMIN_IOPROFILES_RESPONSE] : ({bulkEdit}: AdminIOProfilesState,
    {ioprofiles}: AdminIOProfilesAction) => {
    bulkEdit.loading   = false;
    bulkEdit.isEditing = false;
    bulkEdit.result    = ioprofiles;
  },
  [EDIT_ADMIN_IOPROFILES_ERROR] : ({bulkEdit}: AdminIOProfilesState, {error}: AdminIOProfilesAction) => {
    bulkEdit.loading   = false;
    bulkEdit.error     = error;
  },
  [DELETE_ADMIN_IOPROFILES] : ({bulkDelete}: AdminIOProfilesState,
    {ioprofiles, show}: AdminIOProfilesAction) => {
    bulkDelete.selected  = show ? ioprofiles : null;
    bulkDelete.isEditing = show;
    bulkDelete.result    = null;
  },
  [DELETE_ADMIN_IOPROFILES_REQUEST] : ({bulkDelete}: AdminIOProfilesState) => {
    bulkDelete.loading = true;
    bulkDelete.error   = bulkDelete.result = null;
  },
  [DELETE_ADMIN_IOPROFILES_RESPONSE] : ({bulkDelete}: AdminIOProfilesState,
    {ioprofiles}: AdminIOProfilesAction) => {
    bulkDelete.loading   = false;
    bulkDelete.isEditing = false;
    bulkDelete.result    = ioprofiles;
  },
  [DELETE_ADMIN_IOPROFILES_ERROR] : ({bulkDelete}: AdminIOProfilesState, {error}: AdminIOProfilesAction) => {
    bulkDelete.loading   = false;
    bulkDelete.error     = error;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminIOProfilesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
